"use client";

import { useState, type ReactNode } from "react";

export type AccordionItem = {
  id: string;
  title: ReactNode;
  content: ReactNode;
};

interface SitemapAccordionProps {
  items: AccordionItem[];
  multiple?: boolean;
  defaultOpenIds?: string[];
}

const SitemapAccordion = ({
  items,
  multiple = true,
  defaultOpenIds = [],
}: SitemapAccordionProps) => {
  const [openIds, setOpenIds] = useState<Set<string>>(
    () => new Set(defaultOpenIds),
  );

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(id);

      if (multiple) {
        if (isOpen) next.delete(id);
        else next.add(id);
      } else {
        next.clear();
        if (!isOpen) next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="w-full">
      {items.map((item) => {
        const isOpen = openIds.has(item.id);
        const panelId = `panel-${item.id}`;
        const labelId = `${panelId}-label`;

        return (
          <div key={item.id} className="mt-[10px]">
            <button
              type="button"
              className="w-full cursor-pointer flex items-center p-[5px_15px] bg-light-gray-sitemap"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(item.id)}
            >
              <h2 className="flex justify-between text-[20px] leading-[1.1] my-0 w-full">
                <span id={labelId}>{item.title}</span>
                <span className="min-w-[12px] inline-flex items-center justify-center text-ecolab-blue">
                  {isOpen ? "-" : "+"}
                </span>
              </h2>
            </button>

            <div
              id={panelId}
              aria-labelledby={labelId}
              className={`overflow-hidden mt-1 transition-all duration-600 ease-in-out ${
                isOpen ? "max-h-[20000px] opacity-100" : "max-h-0 opacity-0"
              }`}
              aria-hidden={!isOpen}
              inert={!isOpen ? true : undefined}
            >
              <div className={isOpen ? "mb-[10px]" : ""}>{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SitemapAccordion;
